import { fetchAuthSession } from 'aws-amplify/auth';
import {
  CreateSessionResponse,
  SendMessageResponse,
  GetMessagesResponse,
  SaveJobResponse,
  ChatSession,
} from '@/types/chat';

const API_URL = import.meta.env.VITE_API_URL || 'https://o7pkyged3j.execute-api.us-east-1.amazonaws.com';
const REQUEST_TIMEOUT_MS = 30_000;

async function getAuthToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('Sessão expirada. Faça login novamente.');
  return token;
}

/**
 * Fetch wrapper with 30-second AbortController timeout and user-friendly error messages.
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    return response;
  } catch (error: any) {
    if (error.name === 'AbortError') {
      throw new Error(`A requisição excedeu o tempo limite. Por favor, tente novamente.`);
    }
    throw new Error(`Falha na conexão com o servidor. Verifique sua internet e tente novamente.`);
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Parse an error response body and return a user-friendly message.
 */
async function parseErrorResponse(response: Response, fallbackMessage: string): Promise<string> {
  try {
    const data = await response.json();
    const errorCode = data?.error?.code || '';
    const errorMessage = data?.error?.message || '';

    if (response.status === 401) {
      return 'Sessão expirada. Faça login novamente.';
    }
    if (errorCode === 'QUOTA_EXCEEDED') {
      return 'Você atingiu o limite de vagas do plano gratuito. Acesse Configurações > Assinatura para fazer upgrade para o plano Pro.';
    }
    if (response.status === 403) {
      return 'Você não tem permissão para esta ação.';
    }
    if (response.status === 429 || errorCode === 'RATE_LIMIT_EXCEEDED') {
      return 'Muitas requisições. Aguarde um momento e tente novamente.';
    }
    if (response.status === 404) {
      return 'Sessão não encontrada.';
    }
    if (errorCode === 'AI_BUSY' || errorCode === 'AI_UNAVAILABLE') {
      return errorMessage || 'A IA está ocupada. Tente novamente em instantes.';
    }
    if (errorCode === 'AI_TIMEOUT') {
      return errorMessage || 'A IA demorou muito para responder. Tente novamente.';
    }
    if (response.status === 400) {
      return errorMessage || 'Dados inválidos. Verifique e tente novamente.';
    }

    return errorMessage || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
}

export const chatApi = {
  async createSession(initialData?: Record<string, unknown>): Promise<CreateSessionResponse> {
    const token = await getAuthToken();
    const response = await fetchWithTimeout(
      `${API_URL}/chat/sessions`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ initialData }),
      }
    );

    if (!response.ok) {
      const msg = await parseErrorResponse(response, 'Erro ao iniciar sessão de chat. Tente novamente.');
      throw new Error(msg);
    }

    const data = await response.json();
    return data.data;
  },

  async sendMessage(sessionId: string, message: string): Promise<SendMessageResponse> {
    const token = await getAuthToken();
    const response = await fetchWithTimeout(
      `${API_URL}/chat/sessions/${sessionId}/messages`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ content: message }),
      }
    );

    if (!response.ok) {
      const msg = await parseErrorResponse(response, 'Erro ao enviar mensagem. Tente novamente.');
      throw new Error(msg);
    }

    const data = await response.json();
    return data.data;
  },

  async getMessages(sessionId: string): Promise<GetMessagesResponse> {
    const token = await getAuthToken();
    const response = await fetchWithTimeout(
      `${API_URL}/chat/sessions/${sessionId}/messages`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const msg = await parseErrorResponse(response, 'Erro ao carregar mensagens. Tente novamente.');
      throw new Error(msg);
    }

    const data = await response.json();
    return data.data;
  },

  async listSessions(status?: string): Promise<ChatSession[]> {
    const token = await getAuthToken();
    const url = status 
      ? `${API_URL}/chat/sessions?status=${status}`
      : `${API_URL}/chat/sessions`;
    
    const response = await fetchWithTimeout(
      url,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const msg = await parseErrorResponse(response, 'Erro ao listar sessões. Tente novamente.');
      throw new Error(msg);
    }

    const data = await response.json();
    return data.data;
  },

  async saveJob(sessionId: string): Promise<SaveJobResponse> {
    const token = await getAuthToken();
    const response = await fetchWithTimeout(
      `${API_URL}/chat/sessions/${sessionId}/save-job`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const msg = await parseErrorResponse(
        response,
        'Falha ao salvar vaga. Tente novamente.'
      );
      throw new Error(msg);
    }

    const data = await response.json();
    return data.data;
  },
};
